/**
 * Centralized utilities for reading/writing field values
 *
 * These functions are reused by editor section components to ensure consistent
 * behavior and avoid code duplication.
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import { getValueByPath, getL10nValue, setL10nValue, setValueByPath, isFieldEnabled as baseIsFieldEnabled } from './fieldOperations';
import { routeTypographyOverridePath, type TypographyOverrideProfile } from './v2FieldOperations';

/**
 * Read a style value honoring the active presentation profile (v2.19.1).
 * In profile mode typography paths read the per-profile override first and
 * fall back to the base value — mirrors what the runtime resolver renders.
 */
export function getEffectiveStyleValue<T = unknown>(
  struct: Record<string, unknown>,
  path: string,
  profile?: TypographyOverrideProfile,
): T | undefined {
  if (profile && profile !== 'base') {
    const overridePath = routeTypographyOverridePath(path, profile);
    if (overridePath !== path) {
      const overrideValue = getValueByPath<T>(struct, overridePath);
      if (overrideValue !== undefined && overrideValue !== null) return overrideValue;
    }
  }
  return getValueByPath<T>(struct, path);
}

/**
 * Read display value from struct based on field configuration
 *
 * Handles l10n lookup for localized text fields.
 *
 * @param struct - Screen struct object
 * @param fieldConfig - Field configuration
 * @param locale - Current locale code
 * @returns Display value or empty string if not found
 *
 * @example
 * getFieldDisplayValue(struct, BODY_TITLE_CONFIG, 'en-US')
 * // Returns: 'Welcome'
 */
export function getFieldDisplayValue(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig,
  locale: string
): string {
  // For l10n fields, use l10nPath
  if (fieldConfig.l10nPath) {
    return getL10nValue(struct, fieldConfig.l10nPath, locale);
  }

  // For non-l10n fields, get value directly
  const value = getValueByPath<string>(struct, fieldConfig.path);
  return value ?? '';
}

/**
 * Build update object for onChange handler
 *
 * Constructs the correct path and value for updating struct,
 * handling l10n path construction for localized fields.
 *
 * @param struct - Current struct (for immutable update)
 * @param fieldConfig - Field configuration
 * @param locale - Current locale code
 * @param value - New value to set
 * @returns Updated struct object
 *
 * @example
 * const newStruct = buildFieldUpdate(struct, BODY_TITLE_CONFIG, 'en-US', 'New Title')
 */
export function buildFieldUpdate(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig,
  locale: string,
  value: unknown
): Record<string, unknown> {
  // For l10n fields, update the l10n object
  if (fieldConfig.l10nPath) {
    return setL10nValue(struct, fieldConfig.l10nPath, locale, value as string);
  }

  // For non-l10n fields, update directly
  return setValueByPath(struct, fieldConfig.path, value);
}

/**
 * Read style values for all styleFields in field config
 *
 * Returns a map of style path to value for rendering style editors.
 * Also handles paddingConfig if present (for cross-layout padding fields).
 *
 * @param struct - Screen struct object
 * @param fieldConfig - Field configuration with styleFields and/or paddingConfig
 * @returns Record of style path to value
 *
 * @example
 * getStyleValues(struct, BODY_TITLE_CONFIG)
 * // Returns: { 'title.styles.textColor': '#000000', 'title.styles.fontSize': 16 }
 *
 * @example
 * getStyleValues(struct, BODY_IMAGE_CONFIG)
 * // Returns: { 'image.styles.imageKind': 'cover', 'image.box.styles.paddingTop': 0, ... }
 */
export function getStyleValues(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig,
  profile?: TypographyOverrideProfile
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  // Collect style fields
  if (fieldConfig.styleFields) {
    for (const styleField of fieldConfig.styleFields) {
      const value = getEffectiveStyleValue(struct, styleField.path, profile);
      // For nullable fields, return raw value so StyleFieldRow can determine checkbox state
      // For non-nullable fields, apply defaultValue as fallback
      result[styleField.path] = styleField.isNullable !== false
        ? value
        : (value ?? styleField.defaultValue);
    }
  }

  // Collect padding values from paddingConfig
  if (fieldConfig.paddingConfig) {
    const basePath = fieldConfig.paddingConfig.basePath;
    const usesEdgeInsets = basePath.endsWith('.layout.insets') || basePath.endsWith('.insets');

    if (usesEdgeInsets) {
      result[`${basePath}.top`] = getEffectiveStyleValue(struct, `${basePath}.top`, profile);
      result[`${basePath}.end`] = getEffectiveStyleValue(struct, `${basePath}.end`, profile);
      result[`${basePath}.bottom`] = getEffectiveStyleValue(struct, `${basePath}.bottom`, profile);
      result[`${basePath}.start`] = getEffectiveStyleValue(struct, `${basePath}.start`, profile);
    } else {
      result[`${basePath}.paddingTop`] = getValueByPath(struct, `${basePath}.paddingTop`);
      result[`${basePath}.paddingRight`] = getValueByPath(struct, `${basePath}.paddingRight`);
      result[`${basePath}.paddingBottom`] = getValueByPath(struct, `${basePath}.paddingBottom`);
      result[`${basePath}.paddingLeft`] = getValueByPath(struct, `${basePath}.paddingLeft`);
    }
  }

  return result;
}

/**
 * Read button style values for buttonStyleFields in field config
 *
 * For footer buttons that have separate button container styles.
 *
 * @param struct - Screen struct object
 * @param fieldConfig - Field configuration with buttonStyleFields
 * @returns Record of button style path to value
 */
export function getButtonStyleValues(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig,
  profile?: TypographyOverrideProfile
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (!fieldConfig.buttonStyleFields) {
    return result;
  }

  for (const styleField of fieldConfig.buttonStyleFields) {
    const value = getEffectiveStyleValue(struct, styleField.path, profile);
    // For nullable fields, return raw value so StyleFieldRow can determine checkbox state
    // For non-nullable fields, apply defaultValue as fallback
    result[styleField.path] = styleField.isNullable !== false
      ? value
      : (value ?? styleField.defaultValue);
  }

  // Extract EdgeInsets padding values from buttonStyleGroups paddingConfig
  // (these paths aren't in buttonStyleFields but needed by PaddingElement in ButtonStyleSections)
  if (fieldConfig.buttonStyleGroups) {
    for (const group of fieldConfig.buttonStyleGroups) {
      if (group.paddingConfig) {
        const bp = group.paddingConfig.basePath;
        const insets = getValueByPath(struct, bp) as Record<string, unknown> | undefined;
        if (insets && typeof insets === 'object') {
          result[`${bp}.top`] = getEffectiveStyleValue(struct, `${bp}.top`, profile);
          result[`${bp}.end`] = getEffectiveStyleValue(struct, `${bp}.end`, profile);
          result[`${bp}.bottom`] = getEffectiveStyleValue(struct, `${bp}.bottom`, profile);
          result[`${bp}.start`] = getEffectiveStyleValue(struct, `${bp}.start`, profile);
        }
      }
      // Extract full data object for groups with customComponent (e.g., GradientEditor)
      if (group.objectPath) {
        result[group.objectPath] = getValueByPath(struct, group.objectPath) ?? null;
      }
    }
  }

  return result;
}

/**
 * Check if a field is enabled (not null/undefined)
 *
 * @param struct - Screen struct object
 * @param fieldConfig - Field configuration
 * @returns true if field has a value (not null/undefined)
 */
export function isFieldEnabled(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig
): boolean {
  return baseIsFieldEnabled(struct, fieldConfig.path);
}

/**
 * Get template value from template struct for restoration
 *
 * @param templateStruct - Template struct object
 * @param fieldConfig - Field configuration
 * @param locale - Current locale code
 * @returns Template value or undefined
 */
export function getTemplateValue(
  templateStruct: Record<string, unknown> | undefined,
  fieldConfig: FieldConfig,
  locale: string
): unknown {
  if (!templateStruct) {
    return fieldConfig.defaultValue;
  }

  if (fieldConfig.l10nPath) {
    const value = getL10nValue(templateStruct, fieldConfig.l10nPath, locale);
    return value || fieldConfig.defaultValue;
  }

  const value = getValueByPath(templateStruct, fieldConfig.path);
  return value ?? fieldConfig.defaultValue;
}

// ============================================
// PageIndicator-specific utilities
// ============================================

/**
 * Page Indicator Kind type
 */
export type PageIndicatorKindType = 'Line' | 'Dashes' | 'DashesWithTitle';

/**
 * Get current page indicator kind from struct
 *
 * @param struct - Screen struct object
 * @param kindPath - Path to pageIndicatorKind field
 * @returns Current indicator kind or 'Line' as default
 */
export function getPageIndicatorKind(
  struct: Record<string, unknown>,
  kindPath: string
): PageIndicatorKindType {
  const kind = getValueByPath<string>(struct, kindPath);
  if (kind === 'Dashes' || kind === 'DashesWithTitle') {
    return kind;
  }
  return 'Line';
}

/**
 * Get dashes configuration from struct
 *
 * @param struct - Screen struct object
 * @param dashesPath - Path to dashesPageIndicator field
 * @returns Dashes config with number, height, spacing
 */
export function getDashesConfig(
  struct: Record<string, unknown>,
  dashesPath: string
): { numberOfDashes: number; dashHeight: number; dashesSpacing: number } {
  const dashesObj = getValueByPath<Record<string, unknown>>(struct, dashesPath);

  return {
    numberOfDashes: (dashesObj?.numberOfDashes as number) ?? 5,
    dashHeight: (dashesObj?.dashHeight as number) ?? 1,
    dashesSpacing: (dashesObj?.dashesSpacing as number) ?? 5,
  };
}

/**
 * Get dashes style values from struct
 *
 * @param struct - Screen struct object
 * @param fieldConfig - PageIndicator field configuration with dashesStyleFields
 * @returns Record of dashes style path to value
 */
export function getDashesStyleValues(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (!fieldConfig.dashesStyleFields) {
    return result;
  }

  for (const styleField of fieldConfig.dashesStyleFields) {
    const value = getValueByPath(struct, styleField.path);
    // For nullable fields, return raw value so StyleFieldRow can determine checkbox state
    // For non-nullable fields, apply defaultValue as fallback
    result[styleField.path] = styleField.isNullable !== false
      ? value
      : (value ?? styleField.defaultValue);
  }

  return result;
}

/**
 * Get page indicator line value (0-100)
 *
 * @param struct - Screen struct object
 * @param fieldConfig - PageIndicator field configuration
 * @returns Line value or default from config
 */
export function getPageIndicatorLineValue(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig
): number {
  const valuePath = fieldConfig.valuePath || `${fieldConfig.path}.value`;
  const value = getValueByPath<number>(struct, valuePath);
  return value ?? (fieldConfig.defaultValue as number) ?? 50;
}

/**
 * Get title text value for DashesWithTitle indicator
 *
 * @param struct - Screen struct object
 * @param fieldConfig - PageIndicator field configuration with titleConfig
 * @param locale - Current locale code
 * @returns Title text value
 */
export function getPageIndicatorTitleValue(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig,
  locale: string
): string {
  if (!fieldConfig.titleConfig?.l10nPath) {
    return '';
  }

  return getL10nValue(struct, fieldConfig.titleConfig.l10nPath, locale);
}

/**
 * Check if page indicator title is enabled
 *
 * @param struct - Screen struct object
 * @param fieldConfig - PageIndicator field configuration with titleConfig
 * @returns true if title is enabled
 */
export function isPageIndicatorTitleEnabled(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig
): boolean {
  if (!fieldConfig.titleConfig) {
    return false;
  }

  return baseIsFieldEnabled(struct, fieldConfig.titleConfig.path);
}

/**
 * Get title style values for DashesWithTitle indicator
 *
 * @param struct - Screen struct object
 * @param fieldConfig - PageIndicator field configuration with titleConfig
 * @returns Record of title style path to value
 */
export function getPageIndicatorTitleStyleValues(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (!fieldConfig.titleConfig?.styleFields) {
    return result;
  }

  for (const styleField of fieldConfig.titleConfig.styleFields) {
    const value = getValueByPath(struct, styleField.path);
    // For nullable fields, return raw value so StyleFieldRow can determine checkbox state
    // For non-nullable fields, apply defaultValue as fallback
    result[styleField.path] = styleField.isNullable !== false
      ? value
      : (value ?? styleField.defaultValue);
  }

  return result;
}

// ============================================
// Image-specific utilities
// ============================================

/**
 * AssetUrl structure with multiple resolution URLs
 */
interface AssetUrl {
  thumb?: string;
  small?: string;
  medium?: string;
  normal?: string;
  origin?: string;
}

/**
 * Get image URL from struct (handles l10n.assetUrl object structure)
 *
 * The assetUrl is an object with multiple resolution URLs:
 * { thumb, small, medium, normal, origin }
 *
 * @param struct - Screen struct object
 * @param fieldConfig - Image field configuration
 * @param locale - Current locale code
 * @returns Image URL (origin or normal) or null
 */
export function getImageValue(
  struct: Record<string, unknown>,
  fieldConfig: FieldConfig,
  locale: string
): string | null {
  if (fieldConfig.l10nPath) {
    // For l10n images, assetUrl is an object: { origin, normal, thumb, ... }
    const assetUrlPath = `${fieldConfig.l10nPath}.l10n.${locale}.assetUrl`;
    const assetUrl = getValueByPath<AssetUrl | string>(struct, assetUrlPath);

    if (!assetUrl) return null;

    // Handle string format (simple URL)
    if (typeof assetUrl === 'string') {
      return assetUrl;
    }

    // Handle object format - return origin or normal URL
    return assetUrl.origin ?? assetUrl.normal ?? null;
  }

  const value = getValueByPath<string>(struct, fieldConfig.path);
  return value ?? null;
}

/**
 * Build update object for multi-locale image upload
 *
 * Creates an AssetUrl object and applies it to all specified locales.
 * Used when user chooses "Apply to all locales" in the upload dialog.
 *
 * @param struct - Current struct (for immutable update)
 * @param l10nPath - L10n path for the image field (e.g., 'image')
 * @param locales - Array of locale codes to update
 * @param value - Image URL or null to clear
 * @returns Updated struct object with all locales updated
 *
 * @example
 * const newStruct = buildMultiLocaleImageUpdate(struct, 'image', ['en-US', 'ru-RU'], 'https://...')
 */
export function buildMultiLocaleImageUpdate(
  struct: Record<string, unknown>,
  l10nPath: string,
  locales: string[],
  value: string | null
): Record<string, unknown> {
  let result = struct;

  const assetUrl: AssetUrl | null = value
    ? {
        thumb: value,
        small: value,
        medium: value,
        normal: value,
        origin: value,
      }
    : null;

  for (const locale of locales) {
    const assetUrlPath = `${l10nPath}.l10n.${locale}.assetUrl`;
    result = setValueByPath(result, assetUrlPath, assetUrl);
  }

  return result;
}
